import { PDFDocument, PDFFont, PDFPage, StandardFonts, degrees, rgb } from 'pdf-lib';

/**
 * Text drawn onto the finished pages: page numbers, Bates numbers, watermarks.
 *
 * The awkward part is page rotation. A page carries a /Rotate value that the
 * reader applies when displaying it, but drawing happens in the *unrotated*
 * coordinate space — so a footer placed naively at the bottom of a page turned
 * 90 degrees ends up running down its side. Everything here is therefore
 * expressed in the coordinates the reader will show, then mapped back.
 */

export type StampPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

export interface PageNumberOptions {
  /** Tokens: {n} is this page's number, {N} the total being numbered. */
  format: string;
  position: StampPosition;
  startAt: number;
  fontSize: number;
  /** Distance from the page edge, in points. */
  margin: number;
  /** Useful when the first page is a cover. */
  skipFirstPage: boolean;
}

export interface WatermarkOptions {
  /** Free text, the brand mark, or the mark with a caption under it. */
  kind: 'text' | 'logo' | 'both';
  text: string;
  fontSize: number;
  /** 0 to 1. */
  opacity: number;
  /**
   * Counter-clockwise, as the reader will see it.
   *
   * Only applies to plain text. A tilted logo reads as a mistake rather than a
   * watermark, so anything involving the mark is always drawn upright.
   */
  angle: number;
}

export interface StampOptions {
  pageNumbers?: PageNumberOptions;
  watermark?: WatermarkOptions;
}

export const DEFAULT_PAGE_NUMBERS: PageNumberOptions = {
  format: 'Page {n} of {N}',
  position: 'bottom-center',
  startAt: 1,
  fontSize: 10,
  margin: 28,
  skipFirstPage: false,
};

export const DEFAULT_WATERMARK: WatermarkOptions = {
  kind: 'text',
  text: 'DRAFT',
  fontSize: 72,
  opacity: 0.12,
  angle: 45,
};

/**
 * The ON Services mark, as vector paths.
 *
 * Drawn rather than embedded as an image: it is six flat polygons, so it costs
 * almost nothing, stays crisp at any zoom, and keeps the exact brand colors.
 * Taken from web/public/on-services-mark.svg — if that artwork changes, these
 * must change with it.
 */
const MARK_WIDTH = 498;
const MARK_HEIGHT = 599;
const MARK_PATHS: Array<{ d: string; color: [number, number, number] }> = [
  { d: 'M 13,145 L 208,145 L 493,288 L 345,288 Z', color: [0x78, 0xc2, 0xca] },
  { d: 'M 192,0 L 342,0 L 498,288 L 348,288 Z', color: [0x07, 0x3d, 0x5c] },
  { d: 'M 294,188 L 348,288 L 493,288 Z', color: [0x43, 0x5f, 0x7a] },
  { d: 'M 0,447 L 348,291 L 494,291 L 198,447 Z', color: [0xe4, 0x70, 0x24] },
  { d: 'M 348,291 L 497,293 L 334,599 L 184,598 Z', color: [0xf5, 0xb9, 0x51] },
  { d: 'M 348,291 L 494,291 L 291,398 Z', color: [0xbe, 0x5e, 0x27] },
];

/** The page's size as the reader will see it, which swaps on a quarter turn. */
function displayedSize(page: PDFPage): { width: number; height: number; rotation: number } {
  const { width, height } = page.getSize();
  const rotation = ((page.getRotation().angle % 360) + 360) % 360;
  return rotation % 180 === 90
    ? { width: height, height: width, rotation }
    : { width, height, rotation };
}

/**
 * Maps a point from what the reader sees back to the page's own coordinates.
 *
 * Derived from the four /Rotate cases rather than guessed — the tests place a
 * stamp at every rotation and check where it actually lands.
 */
function toPageSpace(
  page: PDFPage,
  displayedX: number,
  displayedY: number,
): { x: number; y: number } {
  const { width, height } = page.getSize();
  const rotation = ((page.getRotation().angle % 360) + 360) % 360;
  switch (rotation) {
    case 90:
      return { x: width - displayedY, y: displayedX };
    case 180:
      return { x: width - displayedX, y: height - displayedY };
    case 270:
      return { x: displayedY, y: height - displayedX };
    default:
      return { x: displayedX, y: displayedY };
  }
}

function anchorFor(
  position: StampPosition,
  displayedWidth: number,
  displayedHeight: number,
  textWidth: number,
  fontSize: number,
  margin: number,
): { x: number; y: number } {
  const x = position.endsWith('left')
    ? margin
    : position.endsWith('right')
      ? displayedWidth - margin - textWidth
      : (displayedWidth - textWidth) / 2;
  const y = position.startsWith('top') ? displayedHeight - margin - fontSize : margin;
  return { x, y };
}

export async function applyStamps(pdf: PDFDocument, options: StampOptions): Promise<void> {
  if (!options.pageNumbers && !options.watermark) return;

  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const pages = pdf.getPages();

  if (options.pageNumbers) drawPageNumbers(pages, font, options.pageNumbers);

  const mark = options.watermark;
  if (mark?.kind === 'logo' || mark?.kind === 'both') drawLogoWatermark(pages, bold, mark);
  else if (mark?.text.trim()) drawWatermark(pages, bold, mark);
}

/**
 * The brand mark, centered and faint.
 *
 * drawSvgPath anchors the path's own (0,0) — its top-left — at the given point
 * and runs downwards, so the anchor is worked back from where the center needs
 * to land once the whole thing has been rotated.
 */
function drawLogoWatermark(pages: PDFPage[], font: PDFFont, options: WatermarkOptions): void {
  const opacity = Math.min(1, Math.max(0.02, options.opacity));
  const caption = options.kind === 'both' ? options.text.trim() : '';

  for (const page of pages) {
    const { width, height, rotation } = displayedSize(page);

    // Half the shorter side keeps it clear of the margins on any page shape;
    // a caption takes a little of that so the pair still fits.
    const markHeight = Math.min(width, height) * (caption ? 0.42 : 0.5);
    const scale = markHeight / MARK_HEIGHT;
    const markWidth = MARK_WIDTH * scale;

    // Sized against the mark so the pair stays in proportion on any page.
    const captionSize = caption ? markHeight * 0.22 : 0;
    const gap = caption ? markHeight * 0.12 : 0;
    const blockHeight = markHeight + gap + captionSize;

    // Always upright: the mark is never rotated, whatever the angle says.
    const blockTop = height / 2 + blockHeight / 2;
    const markAnchor = toPageSpace(page, width / 2 - markWidth / 2, blockTop);

    for (const shape of MARK_PATHS) {
      page.drawSvgPath(shape.d, {
        x: markAnchor.x,
        y: markAnchor.y,
        scale,
        rotate: degrees(rotation),
        color: rgb(shape.color[0] / 255, shape.color[1] / 255, shape.color[2] / 255),
        opacity,
        borderWidth: 0,
      });
    }

    if (!caption) continue;

    const captionWidth = font.widthOfTextAtSize(caption, captionSize);
    const captionAnchor = toPageSpace(
      page,
      width / 2 - captionWidth / 2,
      blockTop - markHeight - gap - captionSize,
    );
    page.drawText(caption, {
      x: captionAnchor.x,
      y: captionAnchor.y,
      size: captionSize,
      font,
      color: rgb(0.45, 0.45, 0.45),
      opacity,
      rotate: degrees(rotation),
    });
  }
}

function drawPageNumbers(pages: PDFPage[], font: PDFFont, options: PageNumberOptions): void {
  const numbered = options.skipFirstPage ? pages.slice(1) : pages;
  const total = numbered.length;

  numbered.forEach((page, index) => {
    const label = options.format
      .replace(/\{n\}/g, String(options.startAt + index))
      .replace(/\{N\}/g, String(options.startAt + total - 1));
    if (!label) return;

    const { width, height, rotation } = displayedSize(page);
    const textWidth = font.widthOfTextAtSize(label, options.fontSize);
    const anchor = anchorFor(
      options.position,
      width,
      height,
      textWidth,
      options.fontSize,
      options.margin,
    );
    const { x, y } = toPageSpace(page, anchor.x, anchor.y);

    page.drawText(label, {
      x,
      y,
      size: options.fontSize,
      font,
      color: rgb(0.25, 0.25, 0.25),
      // Cancels the page's own rotation so the number reads upright.
      rotate: degrees(rotation),
    });
  });
}

function drawWatermark(pages: PDFPage[], font: PDFFont, options: WatermarkOptions): void {
  const text = options.text.trim();

  for (const page of pages) {
    const { width, height, rotation } = displayedSize(page);
    const textWidth = font.widthOfTextAtSize(text, options.fontSize);

    /*
     * Anchored so the text's midpoint sits at the page's center once rotated.
     * pdf-lib turns text about its start, so the offset is computed along the
     * rotated axis rather than the page's.
     */
    const radians = (options.angle * Math.PI) / 180;
    const centerX = width / 2;
    const centerY = height / 2;
    const anchorX = centerX - (textWidth / 2) * Math.cos(radians) + (options.fontSize / 2) * Math.sin(radians);
    const anchorY = centerY - (textWidth / 2) * Math.sin(radians) - (options.fontSize / 2) * Math.cos(radians);
    const { x, y } = toPageSpace(page, anchorX, anchorY);

    page.drawText(text, {
      x,
      y,
      size: options.fontSize,
      font,
      color: rgb(0.45, 0.45, 0.45),
      opacity: Math.min(1, Math.max(0.02, options.opacity)),
      rotate: degrees(rotation + options.angle),
    });
  }
}
